/**
 * compareAxis.js — Abscisses de la comparaison de sessions (pur, testable).
 *
 * Deux alignements :
 *   - FROM_START : minutes depuis le début de chaque session, courbes rééchantillonnées
 *     sur une grille commune à la minute (axe catégoriel). Deux capteurs démarrés à
 *     des heures différentes voient un même événement à des abscisses différentes.
 *   - CLOCK : horodatage absolu de chaque mesure (axe linéaire en ms), toutes les
 *     sessions sur le même axe : un même instant tombe sur la même abscisse.
 *
 * Fuseau : celui du navigateur (D201), comme la courbe de la page de session
 * (CombinedChart via formatDateLocale) ; D191 ne concerne que l'import.
 * Une mesure absente reste un point y = null : elle délimite un trou (D190).
 */
import { formatDateLocale } from '../utils/formatters';

export const ALIGN_MODES = Object.freeze({ FROM_START: 'fromStart', CLOCK: 'clock' });

const isMeasured = (v) => typeof v === 'number' && Number.isFinite(v);
const toMs = (date) => new Date(date).getTime();

// Points {x = minutes depuis start_time, y} d'une session.
function fromStartPoints(readings, sessionStart, field) {
  const t0 = toMs(sessionStart);
  return readings.map(r => ({
    x: Math.round((toMs(r.timestamp) - t0) / 60000),
    y: isMeasured(r[field]) ? r[field] : null,
  }));
}

// Valeur à x_min donné : point mesuré exact, sinon interpolation linéaire entre
// deux points mesurés CONSÉCUTIFS. Un trou (point null) reste un trou ; rien
// n'est prolongé avant le premier ni après le dernier point.
function valueAt(points, xTarget) {
  const exact = points.find(p => p.x === xTarget && p.y !== null);
  if (exact) return exact.y;
  let i = 0;
  while (i < points.length && points[i].x <= xTarget) i += 1;
  const p1 = points[i - 1];
  const p2 = points[i];
  if (!p1 || !p2 || p1.y === null || p2.y === null) return null;
  const t = (xTarget - p1.x) / (p2.x - p1.x);
  return p1.y + t * (p2.y - p1.y);
}

/**
 * Grille commune en minutes (0 → durée max) et une série rééchantillonnée par
 * session, alignée sur cette grille.
 * @returns {{ labels: number[], series: Array<Array<number|null>> }}
 */
export function buildFromStartSeries(sessions, field) {
  const maxMin = Math.max(0, ...sessions.map(s => {
    const readings = s.readings || [];
    if (!readings.length) return 0;
    const last = toMs(readings[readings.length - 1].timestamp);
    return Math.ceil((last - toMs(s.session.start_time)) / 60000);
  }));
  const labels = Array.from({ length: maxMin + 1 }, (_, i) => i);
  const series = sessions.map(s => {
    const points = fromStartPoints(s.readings || [], s.session.start_time, field);
    return labels.map(x => valueAt(points, x));
  });
  return { labels, series };
}

/**
 * Une série {x = horodatage ms, y} par session, sans rééchantillonnage.
 * @returns {Array<Array<{x: number, y: number|null}>>}
 */
export function buildClockSeries(sessions, field) {
  return sessions.map(s => (s.readings || []).map(r => ({
    x: toMs(r.timestamp),
    y: isMeasured(r[field]) ? r[field] : null,
  })));
}

// [première, dernière] mesure de chaque session qui en porte.
function sessionRanges(sessions) {
  return sessions
    .map(s => s.readings || [])
    .filter(readings => readings.length > 0)
    .map(readings => [toMs(readings[0].timestamp), toMs(readings[readings.length - 1].timestamp)]);
}

/** Bornes de l'axe en heure réelle : { min, max } en ms, ou null sans mesure. */
export function clockBounds(sessions) {
  const ranges = sessionRanges(sessions);
  if (!ranges.length) return null;
  return {
    min: Math.min(...ranges.map(([start]) => start)),
    max: Math.max(...ranges.map(([, end]) => end)),
  };
}

/**
 * Vrai si toutes les sessions partagent une plage horaire commune (intersection
 * non vide). Moins de deux sessions mesurées : rien à comparer, donc vrai.
 */
export function sessionsOverlap(sessions) {
  const ranges = sessionRanges(sessions);
  if (ranges.length < 2) return true;
  const latestStart = Math.max(...ranges.map(([start]) => start));
  const earliestEnd = Math.min(...ranges.map(([, end]) => end));
  return latestStart <= earliestEnd;
}

/** Vrai si la plage affichée tient dans un seul jour calendaire local. */
export function isSingleDay(bounds) {
  if (!bounds) return true;
  const a = new Date(bounds.min);
  const b = new Date(bounds.max);
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

// Pas de graduation « ronds » en minutes : l'axe reste lisible à tout niveau de zoom.
const CLOCK_STEPS_MIN = Object.freeze([1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440]);
const MAX_CLOCK_TICKS = 8;
const MINUTES_PER_HOUR = 60;

// Pas inférieur à l'heure : multiples du pas depuis minuit local. Un changement
// d'heure décale de 60 min, multiple de chacun de ces pas : l'alignement tient.
function subHourTicks(min, max, stepMin) {
  const stepMs = stepMin * 60000;
  const dayStart = new Date(min);
  dayStart.setHours(0, 0, 0, 0);
  const ticks = [];
  for (let v = dayStart.getTime() + Math.ceil((min - dayStart.getTime()) / stepMs) * stepMs; v <= max; v += stepMs) {
    ticks.push({ value: v });
  }
  return ticks;
}

// Pas d'une heure ou plus : heures murales (0 h, 6 h, 12 h…) construites jour
// par jour, jamais par addition de millisecondes, sinon un jour de 23 ou 25 h
// décale toutes les graduations suivantes d'une heure. Une heure absente (passage
// à l'heure d'été) est sautée.
function wallClockTicks(min, max, stepHours) {
  const first = new Date(min);
  const ticks = [];
  for (let day = 0; ; day += 1) {
    const dayStart = new Date(first.getFullYear(), first.getMonth(), first.getDate() + day);
    if (dayStart.getTime() > max) break;
    for (let hour = 0; hour < 24; hour += stepHours) {
      const tick = new Date(first.getFullYear(), first.getMonth(), first.getDate() + day, hour);
      const v = tick.getTime();
      if (tick.getHours() === hour && v >= min && v <= max) ticks.push({ value: v });
    }
  }
  return ticks;
}

/**
 * Graduations en heure réelle sur [min, max] (ms epoch) : plus petit pas rond
 * donnant au plus 8 graduations, calées sur l'heure locale, y compris de part et
 * d'autre d'un changement d'heure.
 * @returns {Array<{value: number}>}
 */
export function clockTicks(min, max) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return [];
  const spanMin = (max - min) / 60000;
  const stepMin = CLOCK_STEPS_MIN.find(step => spanMin / step <= MAX_CLOCK_TICKS)
    || CLOCK_STEPS_MIN[CLOCK_STEPS_MIN.length - 1];
  return stepMin < MINUTES_PER_HOUR
    ? subHourTicks(min, max, stepMin)
    : wallClockTicks(min, max, stepMin / MINUTES_PER_HOUR);
}

/** Graduation en heure réelle : « HH:mm » sur un seul jour, « JJ/MM HH:mm » sinon. */
export function formatClockTick(ms, singleDay) {
  const time = formatDateLocale(ms, { hour: '2-digit', minute: '2-digit' });
  if (singleDay) return time;
  return `${formatDateLocale(ms, { day: '2-digit', month: '2-digit' })} ${time}`;
}
